import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  XMarkIcon,
  Bars3Icon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import categoryService from '../services/categoryService';

/**
 * Sortable Category Item Component
 */
const SortableCategoryItem = ({ category, index, onEdit, onDelete, isSystem }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        isDragging ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-gray-200'
      }`}
    >
      {/* Drag Handle - Only for non-system categories */}
      {!isSystem && (
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
          title="Drag to reorder"
        >
          <Bars3Icon className="h-5 w-5" />
        </div>
      )}
      {isSystem && <div className="w-5 h-5" />} {/* Spacer for system categories */}

      {/* Color Indicator */}
      <div
        className="w-6 h-6 rounded-full border-2 border-gray-300 flex-shrink-0"
        style={{ backgroundColor: category.color || '#6366F1' }}
        title={category.color || '#6366F1'}
      />

      {/* Category Name */}
      <span className="flex-1 text-sm font-medium text-gray-700">
        {category.name}
        {isSystem && (
          <span className="ml-2 text-xs text-gray-500">(System)</span>
        )}
      </span>

      {/* Keywords Preview */}
      {category.keywords && category.keywords.length > 0 && (
        <span className="text-xs text-gray-500">
          {category.keywords.slice(0, 3).join(', ')}
          {category.keywords.length > 3 && '...'}
        </span>
      )}

      {/* Position Indicator */}
      <span className="text-xs text-gray-400">
        {index + 1}
      </span>

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        {!isSystem && (
          <>
            <button
              onClick={() => onEdit(category)}
              className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
              title="Edit category"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(category)}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete category"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Category Management Modal Component
 */
const CategoryManagementModal = ({ isOpen, onClose, onCategoriesUpdate }) => {
  const [categories, setCategories] = useState([]);
  const [localCategories, setLocalCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    color: '#6366F1',
    keywords: '',
    icon: 'tag'
  });

  // Setup sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch categories and preferences when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      fetchCategoryPreferences();
    }
  }, [isOpen]);

  // Fetch saved category preferences
  const fetchCategoryPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE}/api/users/category-preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.preferences?.categoryOrder) {
          // Apply saved order when categories are loaded
          // This will be handled in fetchCategories after categories are set
        }
      }
    } catch (error) {
      console.error('Error fetching category preferences:', error);
      // Not critical, just use default order
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      // Fetch categories (backend will apply user's saved order if available)
      const response = await categoryService.getAllCategories();
      if (response.success) {
        const fetchedCategories = response.data || [];
        // Categories are already sorted by backend (system first, then user categories in saved order)
        setCategories(fetchedCategories);
        setLocalCategories(fetchedCategories);
      } else {
        toast.error('Failed to load categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  // Handle drag end - only allow reordering user categories
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalCategories((items) => {
        const activeItem = items.find(item => item._id === active.id);
        const overItem = items.find(item => item._id === over.id);
        
        // Don't allow moving system categories or moving items into system category positions
        if (activeItem?.isSystem || overItem?.isSystem) {
          return items; // No change
        }

        const oldIndex = items.findIndex(item => item._id === active.id);
        const newIndex = items.findIndex(item => item._id === over.id);
        
        // Only allow reordering within user categories (after system categories)
        const systemCount = items.filter(item => item.isSystem).length;
        if (oldIndex < systemCount || newIndex < systemCount) {
          return items; // Can't move into system category area
        }

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Handle save category order and changes
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Save category order (for user categories only)
      const userCategories = localCategories.filter(cat => !cat.isSystem);
      const categoryOrder = userCategories.map(cat => cat._id);
      
      // Save to user preferences
      await saveCategoryPreferences({ categoryOrder });
      
      toast.success('Category preferences saved successfully');
      
      // Notify parent to refresh categories
      if (onCategoriesUpdate) {
        onCategoriesUpdate();
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving category preferences:', error);
      toast.error('Failed to save category preferences');
    } finally {
      setIsSaving(false);
    }
  };

  // Save category preferences to user profile
  const saveCategoryPreferences = async (preferences) => {
    try {
      const token = localStorage.getItem('token');
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE}/api/users/category-preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ preferences })
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving category preferences:', error);
      throw error;
    }
  };

  // Handle cancel
  const handleCancel = () => {
    // Reset to original categories
    setLocalCategories(categories);
    setShowAddForm(false);
    setEditingCategory(null);
    resetForm();
    onClose();
  };

  // Handle add category
  const handleAddCategory = async (e) => {
    e.preventDefault();
    
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    try {
      setLoading(true);
      const response = await categoryService.createCategory({
        ...newCategory,
        keywords: newCategory.keywords.split(',').map(k => k.trim()).filter(Boolean),
      });
      
      if (response.success) {
        toast.success(`Category "${newCategory.name}" created`);
        resetForm();
        await fetchCategories(); // Refresh list
      } else {
        toast.error(response.message || 'Failed to create category');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit category
  const handleEditCategory = (category) => {
    if (category.isSystem) {
      toast.error('System categories cannot be edited');
      return;
    }
    
    setEditingCategory(category);
    setNewCategory({
      name: category.name,
      color: category.color || '#6366F1',
      keywords: category.keywords?.join(', ') || '',
      icon: category.icon || 'tag'
    });
    setShowAddForm(true);
  };

  // Handle update category
  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    try {
      setLoading(true);
      const response = await categoryService.updateCategory(editingCategory._id, {
        ...newCategory,
        keywords: newCategory.keywords.split(',').map(k => k.trim()).filter(Boolean),
      });
      
      if (response.success) {
        toast.success(`Category "${newCategory.name}" updated`);
        resetForm();
        await fetchCategories(); // Refresh list
      } else {
        toast.error(response.message || 'Failed to update category');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete category
  const handleDeleteCategory = async (category) => {
    if (category.isSystem) {
      toast.error('System categories cannot be deleted');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete the category "${category.name}"?`)) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await categoryService.deleteCategory(category._id);
      
      if (response.success) {
        toast.success(`Category "${category.name}" deleted`);
        await fetchCategories(); // Refresh list
      } else {
        toast.error(response.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewCategory({ name: '', color: '#6366F1', keywords: '', icon: 'tag' });
    setEditingCategory(null);
    setShowAddForm(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Manage Categories
            </h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <p className="text-sm text-gray-600 mb-4">
              Drag categories to reorder them. System categories (shown with "System" label) cannot be reordered, edited, or deleted.
              Custom categories can be edited, deleted, and reordered.
            </p>

            {/* Add/Edit Form */}
            {showAddForm && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {editingCategory ? 'Edit Category' : 'Add New Category'}
                  </h4>
                  <button
                    onClick={resetForm}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <form onSubmit={editingCategory ? handleUpdateCategory : handleAddCategory} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Category name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={newCategory.color}
                          onChange={(e) => setNewCategory({...newCategory, color: e.target.value})}
                          className="h-10 w-10 rounded border-0"
                        />
                        <input
                          type="text"
                          value={newCategory.color}
                          onChange={(e) => setNewCategory({...newCategory, color: e.target.value})}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                          placeholder="#6366F1"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Icon</label>
                      <input
                        type="text"
                        value={newCategory.icon}
                        onChange={(e) => setNewCategory({...newCategory, icon: e.target.value})}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                        placeholder="tag"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Keywords (comma separated)</label>
                      <input
                        type="text"
                        value={newCategory.keywords}
                        onChange={(e) => setNewCategory({...newCategory, keywords: e.target.value})}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                        placeholder="grocery, food, market"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : (editingCategory ? 'Update' : 'Create')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Add Category Button */}
            {!showAddForm && (
              <button
                onClick={() => setShowAddForm(true)}
                className="mb-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                Add Category
              </button>
            )}

            {/* Categories List */}
            {loading && localCategories.length === 0 ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading categories...</p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={localCategories.map(cat => cat._id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {localCategories.map((category, index) => (
                      <SortableCategoryItem
                        key={category._id}
                        category={category}
                        index={index}
                        onEdit={handleEditCategory}
                        onDelete={handleDeleteCategory}
                        isSystem={category.isSystem}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {/* Info */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Category order affects how categories appear in dropdowns and filters.
                Keywords help the system automatically categorize transactions. ML will learn from your category usage patterns.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Save Preferences
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryManagementModal;
